import {
  parseLoomAcceptedDefinitionEvidence,
  type LoomAcceptedDefinitionBinding,
  type LoomV3AdaptationReceipt,
  type LoomV3SourceReceipt,
} from "./loomAcceptedMeshDefinitionEvidence";
import { parseLoomAcceptedUnionReceipt, type LoomAcceptedUnionReceipt } from "./loomAcceptedUnionReceipt";

export const LOOM_ACCEPTED_MESH_SCHEMA = "loom.accepted-mesh/v1" as const;
export const LOOM_ACCEPTED_MESH_SCHEMA_V2 = "loom.accepted-mesh/v2" as const;
export const LOOM_ACCEPTED_MESH_SCHEMA_V3 = "loom.accepted-mesh/v3" as const;
export type LoomAcceptedMeshSchema =
  | typeof LOOM_ACCEPTED_MESH_SCHEMA
  | typeof LOOM_ACCEPTED_MESH_SCHEMA_V2
  | typeof LOOM_ACCEPTED_MESH_SCHEMA_V3;

export type LoomProtectedFeatureKind =
  | "trailing_edge"
  | "boolean_shared_seam"
  | "hard_crease";

export interface LoomAcceptedMeshPatch {
  patchId: string;
  sourceNodeIndex: number;
  definitionId: string;
  evaluatorId: string;
  evaluatorKind: "exact-parametric" | "derived-feature";
  surfaceRole: "upper" | "lower" | "neutral";
  provenance: string;
}

export interface LoomAcceptedMeshSourceNode {
  sourceNodeId: string;
  sourceNodeRevision: string;
  provenance: string;
}

export interface LoomProtectedFeatureChain {
  chainId: string;
  kind: LoomProtectedFeatureKind;
  kutta: boolean;
  adjacentFaceOrdering: "upper-lower" | "unordered";
  edgeOffset: number;
  edgeCount: number;
  closed: boolean;
  patchIndices: number[];
  sourceNodeIndices: number[];
  provenance: string;
}

export interface LoomProtectedFeatureReceipt {
  everyEdgeIsAcceptedFaceEdge: true;
  everyEdgeIsTwoSided: true;
  trailingEdge: {
    allEdgesTwoSided: true;
    orderedUpperLowerFaces: true;
    oppositeSideKuttaFaces: true;
    panelizeTrisPreservesExplicitEdges: true;
  };
  booleanSharedSeam: {
    maxCoordinateGapM: number;
    coordinateToleranceM: number;
    allEdgesTwoSided: true;
    oppositeSourceNodeSides: true;
  };
  hardCrease: { allEdgesTwoSided: true };
}

export interface LoomAcceptedMeshReceipt {
  positionsArray: string;
  facesArray: string;
  geometryRevision: string;
  topologyRevision: string;
}

export interface LoomAcceptedPanelizationReceipt {
  status: "current";
  inputPositionsArray: string;
  inputFacesArray: string;
  inputGeometryRevision: string;
  inputTopologyRevision: string;
  outputPositionsArray: string;
  outputFacesArray: string;
  outputGeometryRevision: string;
  outputTopologyRevision: string;
  staleReason: null;
}

export type LoomAcceptedJacobianReceipt =
  | (LoomAcceptedMeshReceipt & { status: "current"; staleReason: null })
  | (LoomAcceptedMeshReceipt & { status: "stale"; staleReason: string });

export interface LoomAcceptedMeshHeader {
  schema: LoomAcceptedMeshSchema;
  definitionBinding?: LoomAcceptedDefinitionBinding;
  unionAlgorithmId?: "loom.implicit-guided-explicit-union/v1";
  sourceReceipt?: LoomV3SourceReceipt;
  adaptationReceipts?: LoomV3AdaptationReceipt[];
  unionReceipt?: LoomAcceptedUnionReceipt;
  status: "accepted";
  geometryRevision: string;
  topologyRevision: string;
  positionsArray: string;
  facesArray: string;
  facePatchIndicesArray: string;
  faceSourceNodeIndicesArray: string;
  faceCornerParameters: {
    array: string;
    components: ["u", "v"];
    indexSpace: "acceptedGeometry.faces.face-corners";
  };
  patches: LoomAcceptedMeshPatch[];
  sourceNodes: LoomAcceptedMeshSourceNode[];
  protectedFeatures: {
    indexSpace: "acceptedGeometry";
    edgesArray: string;
    adjacentFacesArray: string;
    chainEdgeIndicesArray: string;
    chains: LoomProtectedFeatureChain[];
    receipt: LoomProtectedFeatureReceipt;
  };
  receipts: {
    mesh: LoomAcceptedMeshReceipt;
    panelization: LoomAcceptedPanelizationReceipt;
    jacobian: LoomAcceptedJacobianReceipt;
  };
}

export interface LoomAcceptedMeshHeaderContext {
  arrays: Record<string, unknown>;
  headerAccepted: unknown;
  surfaceAccepted: unknown;
  surfaceQcPassed: unknown;
  surfacePositionsArray: unknown;
  surfaceFacesArray: unknown;
  surfaceVertexCount: unknown;
  surfaceFaceCount: unknown;
  panelAccepted: unknown;
  panelQcPassed: unknown;
  panelVertexCount: unknown;
  panelFaceCount: unknown;
  panelPositionsArray: unknown;
  panelFacesArray: unknown;
}

interface ArrayDescriptor {
  dtype: "float32" | "uint32";
  count: number;
  shape: number[];
}

function record(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${label} must be an object`);
  }
  return value as Record<string, unknown>;
}

function exact(value: unknown, keys: readonly string[], label: string): Record<string, unknown> {
  const result = record(value, label);
  const actual = Object.keys(result);
  if (actual.length !== keys.length || actual.some((key) => !keys.includes(key))) {
    throw new Error(`${label} has an unsupported or incomplete v1 field set`);
  }
  return result;
}

function string(value: unknown, label: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`${label} must be a non-empty string`);
  }
  return value;
}

function revision(value: unknown, label: string): string {
  const result = string(value, label);
  if (!/^sha256:[0-9a-f]{64}$/.test(result)) throw new Error(`${label} must be a sha256 revision`);
  return result;
}

export function isLoomAcceptedMeshSchema(value: unknown): value is LoomAcceptedMeshSchema {
  return value === LOOM_ACCEPTED_MESH_SCHEMA || value === LOOM_ACCEPTED_MESH_SCHEMA_V2
    || value === LOOM_ACCEPTED_MESH_SCHEMA_V3;
}

function integer(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
    throw new Error(`${label} must be a non-negative safe integer`);
  }
  return value;
}

function nonnegativeNumber(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    throw new Error(`${label} must be a finite non-negative number`);
  }
  return value;
}

function integerList(value: unknown, label: string, maximum: number): number[] {
  if (!Array.isArray(value) || value.length === 0) throw new Error(`${label} must be a non-empty array`);
  const result = value.map((item, index) => integer(item, `${label}[${index}]`));
  if (result.some((item) => item >= maximum)) throw new Error(`${label} contains an out-of-range index`);
  if (new Set(result).size !== result.length) throw new Error(`${label} contains duplicate indices`);
  return result;
}

function descriptor(
  arrays: Record<string, unknown>,
  name: unknown,
  dtype: ArrayDescriptor["dtype"],
  shape: readonly number[],
  label: string,
): string {
  const arrayName = string(name, `${label}.array`);
  const raw = record(arrays[arrayName], `${label} descriptor`);
  if (raw.dtype !== dtype) throw new Error(`${label} must reference a ${dtype} array`);
  if (!Array.isArray(raw.shape) || raw.shape.length !== shape.length
      || raw.shape.some((value, index) => value !== shape[index])) {
    throw new Error(`${label} has an invalid shape`);
  }
  const expectedCount = shape.reduce((product, value) => product * value, 1);
  if (raw.count !== expectedCount) throw new Error(`${label} has an invalid count`);
  return arrayName;
}

function meshReceipt(value: unknown, label: string): LoomAcceptedMeshReceipt {
  const raw = exact(value, ["positionsArray", "facesArray", "geometryRevision", "topologyRevision"], label);
  return {
    positionsArray: string(raw.positionsArray, `${label}.positionsArray`),
    facesArray: string(raw.facesArray, `${label}.facesArray`),
    geometryRevision: revision(raw.geometryRevision, `${label}.geometryRevision`),
    topologyRevision: revision(raw.topologyRevision, `${label}.topologyRevision`),
  };
}

function sameMeshReceipt(
  receipt: LoomAcceptedMeshReceipt,
  expected: LoomAcceptedMeshReceipt,
  label: string,
): void {
  for (const key of ["positionsArray", "facesArray", "geometryRevision", "topologyRevision"] as const) {
    if (receipt[key] !== expected[key]) throw new Error(`${label}.${key} is stale or mismatched`);
  }
}

function patch(value: unknown, index: number, sourceNodeCount: number): LoomAcceptedMeshPatch {
  const raw = exact(value, ["patchId", "sourceNodeIndex", "definitionId", "evaluatorId", "evaluatorKind", "surfaceRole", "provenance"], `acceptedGeometry.patches[${index}]`);
  const sourceNodeIndex = integer(raw.sourceNodeIndex, `acceptedGeometry.patches[${index}].sourceNodeIndex`);
  if (sourceNodeIndex >= sourceNodeCount) throw new Error("acceptedGeometry patch source-node index is out of range");
  if (raw.evaluatorKind !== "exact-parametric" && raw.evaluatorKind !== "derived-feature") {
    throw new Error("acceptedGeometry patch evaluator kind is invalid");
  }
  if (raw.surfaceRole !== "upper" && raw.surfaceRole !== "lower" && raw.surfaceRole !== "neutral") {
    throw new Error("acceptedGeometry patch surface role is invalid");
  }
  return {
    patchId: string(raw.patchId, `acceptedGeometry.patches[${index}].patchId`),
    sourceNodeIndex,
    definitionId: string(raw.definitionId, `acceptedGeometry.patches[${index}].definitionId`),
    evaluatorId: string(raw.evaluatorId, `acceptedGeometry.patches[${index}].evaluatorId`),
    evaluatorKind: raw.evaluatorKind,
    surfaceRole: raw.surfaceRole,
    provenance: string(raw.provenance, `acceptedGeometry.patches[${index}].provenance`),
  };
}

function sourceNode(value: unknown, index: number): LoomAcceptedMeshSourceNode {
  const raw = exact(value, ["sourceNodeId", "sourceNodeRevision", "provenance"], `acceptedGeometry.sourceNodes[${index}]`);
  return {
    sourceNodeId: string(raw.sourceNodeId, `acceptedGeometry.sourceNodes[${index}].sourceNodeId`),
    sourceNodeRevision: string(raw.sourceNodeRevision, `acceptedGeometry.sourceNodes[${index}].sourceNodeRevision`),
    provenance: string(raw.provenance, `acceptedGeometry.sourceNodes[${index}].provenance`),
  };
}

function chain(
  value: unknown,
  index: number,
  patchCount: number,
  sourceNodeCount: number,
): LoomProtectedFeatureChain {
  const raw = exact(value, ["chainId", "kind", "kutta", "adjacentFaceOrdering", "edgeOffset", "edgeCount", "closed", "patchIndices", "sourceNodeIndices", "provenance"], `acceptedGeometry.protectedFeatures.chains[${index}]`);
  if (raw.kind !== "trailing_edge" && raw.kind !== "boolean_shared_seam" && raw.kind !== "hard_crease") {
    throw new Error("acceptedGeometry protected feature kind is unsupported");
  }
  if (typeof raw.kutta !== "boolean" || typeof raw.closed !== "boolean") {
    throw new Error("acceptedGeometry protected feature flags are invalid");
  }
  if (raw.adjacentFaceOrdering !== "upper-lower" && raw.adjacentFaceOrdering !== "unordered") {
    throw new Error("acceptedGeometry protected feature face ordering is invalid");
  }
  if (raw.kind === "trailing_edge") {
    if (!raw.kutta || raw.adjacentFaceOrdering !== "upper-lower" || raw.closed) {
      throw new Error("acceptedGeometry trailing edge must be an open, upper/lower-ordered Kutta chain");
    }
  } else if (raw.kutta || raw.adjacentFaceOrdering !== "unordered") {
    throw new Error("acceptedGeometry non-trailing feature cannot claim Kutta ordering");
  }
  const edgeCount = integer(raw.edgeCount, `acceptedGeometry.protectedFeatures.chains[${index}].edgeCount`);
  if (edgeCount === 0) throw new Error("acceptedGeometry protected feature chains cannot be empty");
  return {
    chainId: string(raw.chainId, `acceptedGeometry.protectedFeatures.chains[${index}].chainId`),
    kind: raw.kind,
    kutta: raw.kutta,
    adjacentFaceOrdering: raw.adjacentFaceOrdering,
    edgeOffset: integer(raw.edgeOffset, `acceptedGeometry.protectedFeatures.chains[${index}].edgeOffset`),
    edgeCount,
    closed: raw.closed,
    patchIndices: integerList(raw.patchIndices, `acceptedGeometry.protectedFeatures.chains[${index}].patchIndices`, patchCount),
    sourceNodeIndices: integerList(raw.sourceNodeIndices, `acceptedGeometry.protectedFeatures.chains[${index}].sourceNodeIndices`, sourceNodeCount),
    provenance: string(raw.provenance, `acceptedGeometry.protectedFeatures.chains[${index}].provenance`),
  };
}

function protectedReceipt(value: unknown): LoomProtectedFeatureReceipt {
  const raw = exact(value, ["everyEdgeIsAcceptedFaceEdge", "everyEdgeIsTwoSided", "trailingEdge", "booleanSharedSeam", "hardCrease"], "acceptedGeometry.protectedFeatures.receipt");
  const trailing = exact(raw.trailingEdge, ["allEdgesTwoSided", "orderedUpperLowerFaces", "oppositeSideKuttaFaces", "panelizeTrisPreservesExplicitEdges"], "acceptedGeometry.protectedFeatures.receipt.trailingEdge");
  const seam = exact(raw.booleanSharedSeam, ["maxCoordinateGapM", "coordinateToleranceM", "allEdgesTwoSided", "oppositeSourceNodeSides"], "acceptedGeometry.protectedFeatures.receipt.booleanSharedSeam");
  const crease = exact(raw.hardCrease, ["allEdgesTwoSided"], "acceptedGeometry.protectedFeatures.receipt.hardCrease");
  if (raw.everyEdgeIsAcceptedFaceEdge !== true || raw.everyEdgeIsTwoSided !== true
      || trailing.allEdgesTwoSided !== true || trailing.orderedUpperLowerFaces !== true
      || trailing.oppositeSideKuttaFaces !== true || trailing.panelizeTrisPreservesExplicitEdges !== true
      || seam.allEdgesTwoSided !== true || seam.oppositeSourceNodeSides !== true
      || crease.allEdgesTwoSided !== true) {
    throw new Error("acceptedGeometry protected-feature receipt did not pass");
  }
  const maxCoordinateGapM = nonnegativeNumber(
    seam.maxCoordinateGapM,
    "acceptedGeometry.protectedFeatures.receipt.booleanSharedSeam.maxCoordinateGapM",
  );
  const coordinateToleranceM = nonnegativeNumber(
    seam.coordinateToleranceM,
    "acceptedGeometry.protectedFeatures.receipt.booleanSharedSeam.coordinateToleranceM",
  );
  if (maxCoordinateGapM > coordinateToleranceM) {
    throw new Error("acceptedGeometry shared-seam coordinate receipt is invalid");
  }
  return {
    everyEdgeIsAcceptedFaceEdge: true,
    everyEdgeIsTwoSided: true,
    trailingEdge: {
      allEdgesTwoSided: true,
      orderedUpperLowerFaces: true,
      oppositeSideKuttaFaces: true,
      panelizeTrisPreservesExplicitEdges: true,
    },
    booleanSharedSeam: { maxCoordinateGapM, coordinateToleranceM, allEdgesTwoSided: true, oppositeSourceNodeSides: true },
    hardCrease: { allEdgesTwoSided: true },
  };
}

export function parseLoomAcceptedMeshHeader(
  value: unknown,
  context: LoomAcceptedMeshHeaderContext,
): LoomAcceptedMeshHeader {
  const recordValue = record(value, "acceptedGeometry");
  const schema = recordValue.schema;
  if (!isLoomAcceptedMeshSchema(schema)) {
    throw new Error("Loom accepted geometry is missing, provisional, stale, or unsupported");
  }
  const baseKeys = ["schema", "status", "geometryRevision", "topologyRevision", "positionsArray", "facesArray", "facePatchIndicesArray", "faceSourceNodeIndicesArray", "faceCornerParameters", "patches", "sourceNodes", "protectedFeatures", "receipts"];
  const raw = exact(value, schema === LOOM_ACCEPTED_MESH_SCHEMA_V3
    ? [...baseKeys, "definitionBinding", "unionAlgorithmId", "sourceReceipt", "adaptationReceipts", "unionReceipt"]
    : schema === LOOM_ACCEPTED_MESH_SCHEMA_V2 ? [...baseKeys, "definitionBinding"]
      : baseKeys, "acceptedGeometry");
  if (raw.status !== "accepted") {
    throw new Error("Loom accepted geometry is missing, provisional, stale, or unsupported");
  }
  const evidence = parseLoomAcceptedDefinitionEvidence(schema, raw);
  if (context.headerAccepted !== true || context.surfaceAccepted !== true || context.surfaceQcPassed !== true
      || context.panelAccepted !== true || context.panelQcPassed !== true) {
    throw new Error("Loom accepted geometry cannot bind an unaccepted source or panel mesh");
  }
  const vertexCount = integer(context.panelVertexCount, "panel.vertexCount");
  const faceCount = integer(context.panelFaceCount, "panel.faceCount");
  const geometryRevision = revision(raw.geometryRevision, "acceptedGeometry.geometryRevision");
  const topologyRevision = revision(raw.topologyRevision, "acceptedGeometry.topologyRevision");
  const positionsArray = descriptor(context.arrays, raw.positionsArray, "float32", [vertexCount, 3], "acceptedGeometry.positionsArray");
  const facesArray = descriptor(context.arrays, raw.facesArray, "uint32", [faceCount, 3], "acceptedGeometry.facesArray");
  if (positionsArray !== context.panelPositionsArray || facesArray !== context.panelFacesArray) {
    throw new Error("acceptedGeometry must reference the feature-constrained panel mesh");
  }
  const sourceNodesRaw = raw.sourceNodes;
  if (!Array.isArray(sourceNodesRaw) || sourceNodesRaw.length === 0) throw new Error("acceptedGeometry.sourceNodes must be non-empty");
  const sourceNodes = sourceNodesRaw.map(sourceNode);
  const patchesRaw = raw.patches;
  if (!Array.isArray(patchesRaw) || patchesRaw.length === 0) throw new Error("acceptedGeometry.patches must be non-empty");
  const patches = patchesRaw.map((item, index) => patch(item, index, sourceNodes.length));
  for (const [label, ids] of [["patch", patches.map((item) => item.patchId)], ["source-node", sourceNodes.map((item) => item.sourceNodeId)]] as const) {
    if (new Set(ids).size !== ids.length) throw new Error(`acceptedGeometry has duplicate ${label} IDs`);
  }
  const unionReceipt = schema === LOOM_ACCEPTED_MESH_SCHEMA_V3
    ? parseLoomAcceptedUnionReceipt(raw.unionReceipt, {
      arrays: context.arrays, surfaceVertexCount: integer(context.surfaceVertexCount, "surface.vertexCount"),
      surfaceFaceCount: integer(context.surfaceFaceCount, "surface.faceCount"),
      sourceNodeCount: sourceNodes.length, patchCount: patches.length,
      definitionRevision: evidence.definitionBinding!.definitionRevision,
    }) : undefined;
  const facePatchIndicesArray = descriptor(context.arrays, raw.facePatchIndicesArray, "uint32", [faceCount], "acceptedGeometry.facePatchIndicesArray");
  const faceSourceNodeIndicesArray = descriptor(context.arrays, raw.faceSourceNodeIndicesArray, "uint32", [faceCount], "acceptedGeometry.faceSourceNodeIndicesArray");
  const parameters = exact(raw.faceCornerParameters, ["array", "components", "indexSpace"], "acceptedGeometry.faceCornerParameters");
  if (!Array.isArray(parameters.components) || parameters.components.length !== 2
      || parameters.components[0] !== "u" || parameters.components[1] !== "v"
      || parameters.indexSpace !== "acceptedGeometry.faces.face-corners") {
    throw new Error("acceptedGeometry face-corner parameter semantics are invalid");
  }
  const faceCornerParametersArray = descriptor(context.arrays, parameters.array, "float32", [faceCount, 3, 2], "acceptedGeometry.faceCornerParameters");
  const protectedRaw = exact(raw.protectedFeatures, ["indexSpace", "edgesArray", "adjacentFacesArray", "chainEdgeIndicesArray", "chains", "receipt"], "acceptedGeometry.protectedFeatures");
  if (protectedRaw.indexSpace !== "acceptedGeometry") throw new Error("acceptedGeometry protected-feature index space is invalid");
  if (!Array.isArray(protectedRaw.chains) || protectedRaw.chains.length === 0) throw new Error("acceptedGeometry protected feature chains are missing");
  const chains = protectedRaw.chains.map((item, index) => chain(item, index, patches.length, sourceNodes.length));
  const featureReceipt = protectedReceipt(protectedRaw.receipt);
  if (new Set(chains.map((item) => item.chainId)).size !== chains.length) throw new Error("acceptedGeometry has duplicate protected chain IDs");
  for (const kind of ["trailing_edge", "boolean_shared_seam", "hard_crease"] as const) {
    if (!chains.some((item) => item.kind === kind)) throw new Error(`acceptedGeometry is missing protected ${kind} chains`);
  }
  const chainEntryCount = chains.reduce((sum, item) => sum + item.edgeCount, 0);
  let expectedOffset = 0;
  for (const item of chains) {
    if (item.edgeOffset !== expectedOffset) throw new Error("acceptedGeometry protected chain slices must be contiguous and ordered");
    expectedOffset += item.edgeCount;
  }
  const edgesArrayRaw = string(protectedRaw.edgesArray, "acceptedGeometry.protectedFeatures.edgesArray");
  const edgesDescriptor = record(context.arrays[edgesArrayRaw], "acceptedGeometry protected edges descriptor");
  if (edgesDescriptor.dtype !== "uint32" || !Array.isArray(edgesDescriptor.shape)
      || edgesDescriptor.shape.length !== 2 || edgesDescriptor.shape[1] !== 2) {
    throw new Error("acceptedGeometry protected edges must have uint32 [edge,2] shape");
  }
  const edgeCount = integer(edgesDescriptor.shape[0], "acceptedGeometry protected edge count");
  const edgesArray = descriptor(context.arrays, edgesArrayRaw, "uint32", [edgeCount, 2], "acceptedGeometry.protectedFeatures.edgesArray");
  const adjacentFacesArray = descriptor(context.arrays, protectedRaw.adjacentFacesArray, "uint32", [edgeCount, 2], "acceptedGeometry.protectedFeatures.adjacentFacesArray");
  const chainEdgeIndicesArray = descriptor(context.arrays, protectedRaw.chainEdgeIndicesArray, "uint32", [chainEntryCount], "acceptedGeometry.protectedFeatures.chainEdgeIndicesArray");
  const receipts = exact(raw.receipts, ["mesh", "panelization", "jacobian"], "acceptedGeometry.receipts");
  const expectedMesh = { positionsArray, facesArray, geometryRevision, topologyRevision };
  const mesh = meshReceipt(receipts.mesh, "acceptedGeometry.receipts.mesh");
  sameMeshReceipt(mesh, expectedMesh, "acceptedGeometry.receipts.mesh");
  const panelRaw = exact(receipts.panelization, ["status", "inputPositionsArray", "inputFacesArray", "inputGeometryRevision", "inputTopologyRevision", "outputPositionsArray", "outputFacesArray", "outputGeometryRevision", "outputTopologyRevision", "staleReason"], "acceptedGeometry.receipts.panelization");
  if (panelRaw.status !== "current" || panelRaw.staleReason !== null) throw new Error("acceptedGeometry panelization receipt is stale");
  const panelization: LoomAcceptedPanelizationReceipt = {
    status: "current",
    inputPositionsArray: string(panelRaw.inputPositionsArray, "acceptedGeometry.receipts.panelization.inputPositionsArray"),
    inputFacesArray: string(panelRaw.inputFacesArray, "acceptedGeometry.receipts.panelization.inputFacesArray"),
    inputGeometryRevision: revision(panelRaw.inputGeometryRevision, "acceptedGeometry.receipts.panelization.inputGeometryRevision"),
    inputTopologyRevision: revision(panelRaw.inputTopologyRevision, "acceptedGeometry.receipts.panelization.inputTopologyRevision"),
    outputPositionsArray: string(panelRaw.outputPositionsArray, "acceptedGeometry.receipts.panelization.outputPositionsArray"),
    outputFacesArray: string(panelRaw.outputFacesArray, "acceptedGeometry.receipts.panelization.outputFacesArray"),
    outputGeometryRevision: revision(panelRaw.outputGeometryRevision, "acceptedGeometry.receipts.panelization.outputGeometryRevision"),
    outputTopologyRevision: revision(panelRaw.outputTopologyRevision, "acceptedGeometry.receipts.panelization.outputTopologyRevision"),
    staleReason: null,
  };
  if (panelization.inputPositionsArray !== context.surfacePositionsArray || panelization.inputFacesArray !== context.surfaceFacesArray
      || panelization.outputPositionsArray !== positionsArray || panelization.outputFacesArray !== facesArray
      || panelization.outputGeometryRevision !== geometryRevision || panelization.outputTopologyRevision !== topologyRevision) {
    throw new Error("acceptedGeometry panelization receipt does not bind exact input to accepted output");
  }
  const jacobianRaw = exact(receipts.jacobian, ["status", "positionsArray", "facesArray", "geometryRevision", "topologyRevision", "staleReason"], "acceptedGeometry.receipts.jacobian");
  const jacobianMesh = meshReceipt({
    positionsArray: jacobianRaw.positionsArray,
    facesArray: jacobianRaw.facesArray,
    geometryRevision: jacobianRaw.geometryRevision,
    topologyRevision: jacobianRaw.topologyRevision,
  }, "acceptedGeometry.receipts.jacobian");
  let jacobian: LoomAcceptedJacobianReceipt;
  if (jacobianRaw.status === "current") {
    if (jacobianRaw.staleReason !== null) throw new Error("acceptedGeometry current Jacobian receipt has a stale reason");
    sameMeshReceipt(jacobianMesh, expectedMesh, "acceptedGeometry.receipts.jacobian");
    jacobian = { ...jacobianMesh, status: "current", staleReason: null };
  } else if (jacobianRaw.status === "stale") {
    const staleReason = string(jacobianRaw.staleReason, "acceptedGeometry.receipts.jacobian.staleReason");
    if (Object.keys(expectedMesh).every((key) => jacobianMesh[key as keyof LoomAcceptedMeshReceipt] === expectedMesh[key as keyof LoomAcceptedMeshReceipt])) {
      throw new Error("acceptedGeometry Jacobian cannot claim stale while matching the accepted mesh");
    }
    if (jacobianMesh.positionsArray !== panelization.inputPositionsArray
        || jacobianMesh.facesArray !== panelization.inputFacesArray
        || jacobianMesh.geometryRevision !== panelization.inputGeometryRevision
        || jacobianMesh.topologyRevision !== panelization.inputTopologyRevision) {
      throw new Error("acceptedGeometry stale Jacobian receipt is not bound to the exact-surface input");
    }
    jacobian = { ...jacobianMesh, status: "stale", staleReason };
  } else throw new Error("acceptedGeometry Jacobian receipt status is invalid");
  return {
    schema,
    ...evidence,
    ...(unionReceipt ? { unionReceipt } : {}),
    status: "accepted",
    geometryRevision,
    topologyRevision,
    positionsArray,
    facesArray,
    facePatchIndicesArray,
    faceSourceNodeIndicesArray,
    faceCornerParameters: { array: faceCornerParametersArray, components: ["u", "v"], indexSpace: "acceptedGeometry.faces.face-corners" },
    patches,
    sourceNodes,
    protectedFeatures: { indexSpace: "acceptedGeometry", edgesArray, adjacentFacesArray, chainEdgeIndicesArray, chains, receipt: featureReceipt },
    receipts: { mesh, panelization, jacobian },
  };
}
